import { useCallback } from 'react'
import type { IframeMessage } from '../types/messages'
import { log, LogCategory } from '../lib/logger'

/**
 * useParentCommunication
 *
 * Hook for sending messages from map-editor iframe to parent application.
 * Handles origin validation and provides type-safe message sending.
 *
 * Usage:
 * ```tsx
 * const { sendMessage } = useParentCommunication()
 *
 * const handleSave = async () => {
 *   try {
 *     await saveProject(data)
 *     sendMessage({ type: 'SAVE_SUCCESS', projectId: '123', data })
 *   } catch (error) {
 *     sendMessage({ type: 'SAVE_ERROR', error: error.message })
 *   }
 * }
 * ```
 */
export function useParentCommunication() {
  const sendMessage = useCallback((message: IframeMessage) => {
    // Only send if running in iframe
    if (window.parent === window) {
      log.warn(LogCategory.GENERAL, 'useParentCommunication: Not in iframe, message not sent', { message })
      return
    }

    // Get expected parent origin from environment
    const targetOrigin = process.env.REACT_APP_PARENT_ORIGIN || '*'

    // Send message to parent
    window.parent.postMessage(message, targetOrigin)

    log.info(LogCategory.GENERAL, 'useParentCommunication: Message sent to parent', {
      type: message.type,
      targetOrigin,
    })
  }, [])

  return { sendMessage }
}
